import { InputCard } from '@/components/input-card';
import { RoundedButton } from '@/components/rounded-button';
import { getVaultMetadataByAddress } from '@/constants/nucleus-vaults';
import { AppRoute, STALE_TIME_VERY_SHORT } from '@/constants/shared';
import { Box, HStack, Text } from '@chakra-ui/react';
import { getVaultByKey } from '@molecularlabs/nucleus-frontend';
import { useAppKitAccount, useAppKitNetwork } from '@reown/appkit/react';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { erc20Abi, formatUnits } from 'viem';
import { useReadContract } from 'wagmi';
import { AssetSelect } from '../../AssetSelect';
import { DepositsAndWithdrawalFrame } from '../DepositAndWithdrawalFrame';
import { useDeposits } from '../hooks/useDeposits';
import { DepositSteps } from './DepositSteps';

export const DepositToVault = () => {
  const [showDepositSteps, setShowDepositSteps] = useState(false);
  const [amount, setAmount] = useState('');
  const [depositToken, setDepositToken] = useState<{ symbol: string; address: `0x${string}`; decimals: number }>();
  const { chainId } = useAppKitNetwork();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const roycoOnly = searchParams.get('royco_only') === 'true';
  const { address } = useAppKitAccount();
  const { vaultAddress } = useParams() as { vaultAddress: EthAddress };
  const vaultMetadata = getVaultMetadataByAddress(vaultAddress);
  const { vaultKey, name } = vaultMetadata;
  const { exchangeRate } = useDeposits({ amount: Number(amount), depositToken });
  const {
    data: vault,
    error: vaultError,
    status: vaultStatus,
  } = useQuery({
    queryKey: ['vault', vaultKey],
    queryFn: () => getVaultByKey(vaultKey),
  });

  const { data: depositTokenBalance, isLoading: isLoadingDepositTokenBalance } = useReadContract({
    abi: erc20Abi,
    // TODO: replace when we get proper obj back from vault response
    // we need to use the selected token address
    address: depositToken?.address,
    args: [address as EthAddress],
    functionName: 'balanceOf',
    query: {
      refetchInterval: STALE_TIME_VERY_SHORT,
    },
  });

  const { data: depositTokenDecimals } = useReadContract({
    abi: erc20Abi,
    address: depositToken?.address,
    functionName: 'decimals',
  });

  const formattedDepositTokenBalance = useMemo(() => {
    if (!depositTokenBalance || !depositTokenDecimals) return 0;
    return Number(formatUnits(depositTokenBalance, depositTokenDecimals));
  }, [depositTokenBalance, depositTokenDecimals]);

  const depositAssets = useMemo(() => {
    if (!chainId || !vault) return [];
    if (roycoOnly) return [vault.token];

    return Object.values(vault?.deposit?.sourceChains?.[chainId]?.depositTokens ?? []);
  }, [chainId, roycoOnly, vault]);

  useEffect(() => {
    setDepositToken(depositAssets[0]);
  }, [depositAssets, setDepositToken]);

  if (vaultError) {
    // replace with error screen
    return <div>Error: {vaultError.message}</div>;
  }

  if (vaultStatus === 'pending') {
    // use skeleton loader
    return <div>Loading...</div>;
  }

  return (
    <DepositsAndWithdrawalFrame title={`Deposit into ${roycoOnly ? 'Royco Market' : name}`}>
      {showDepositSteps ? (
        <DepositSteps
          amount={Number(amount)}
          depositToken={depositToken}
          vault={vault}
          onCancel={() => navigate(`${AppRoute.Optimizer}/${vaultAddress}`)}
        />
      ) : (
        <Box w={['full', 'full', 480]}>
          <InputCard
            balance={formattedDepositTokenBalance}
            value={amount}
            loading={isLoadingDepositTokenBalance}
            onChange={setAmount}
            endElement={
              <AssetSelect
                selectedToken={depositToken}
                setSelectedToken={setDepositToken}
                tokenOptions={depositAssets}
              />
            }
          />
          <RoundedButton onClick={() => setShowDepositSteps(true)} w="full">
            Deposit
          </RoundedButton>
          {exchangeRate ? (
            <DepositDetailRow
              title="Exchange Rate"
              value={`1 ${vaultKey} = ${1 / exchangeRate} ${depositToken?.symbol}`}
            />
          ) : null}
          <DepositDetailRow title="Estimated Processing Time" value="10-15 seconds" />
          <DepositDetailRow title="Network Fee" value="$0.001" />
        </Box>
      )}
    </DepositsAndWithdrawalFrame>
  );
};

const DepositDetailRow = ({ title, value }: { title: string; value: string }) => (
  <HStack justifyContent="space-between" mt={4}>
    <Text fontSize="sm" color="fg.muted" fontWeight="400">
      {title}
    </Text>
    <Text fontSize="sm" color="fg" fontWeight="400">
      {value}
    </Text>
  </HStack>
);
